import { useState } from 'react';
import useSWR from 'swr';
import axiosInstance from '@/lib/axios';
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Clock3, Eye, Loader2, RefreshCw, Ticket as TicketIcon } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface SelfMarkActivity {
  id: string;
  name: string;
  selfMarkButtonLabel: string;
  selfMarkRequiresConfirmation: boolean;
  myScan: {
    id: string;
    status: 'PENDING' | 'CONFIRMED';
    scannedAt: string;
    confirmedAt: string | null;
  } | null;
}

interface RegisteredCardProps {
  eventId: string;
  registrationsCount: number;
  isSelfAttending?: boolean;
  onViewTicket: () => void;
}

export function RegisteredCard({
  eventId,
  registrationsCount,
  isSelfAttending = false,
  onViewTicket,
}: RegisteredCardProps) {
  const { data, mutate, isValidating } = useSWR<{ activities: SelfMarkActivity[] }>(
    isSelfAttending ? `/api/events/${eventId}/self-mark-activities` : null,
    (url: string) => axiosInstance.get(url).then((res) => res.data),
    { refreshInterval: 5000 }
  );
  const selfMarkActivities = data?.activities ?? [];
  const [submittingId, setSubmittingId] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const refreshAttendance = async () => {
    setIsRefreshing(true);
    try {
      await mutate();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSelfMark = async (activity: SelfMarkActivity) => {
    setSubmittingId(activity.id);
    try {
      const response = await axiosInstance.post(
        `/api/events/${eventId}/activities/${activity.id}/self-mark`
      );
      if (response.data.requiresConfirmation) {
        toast.success('Marked — waiting for confirmation');
      } else {
        toast.success('Checked in successfully');
      }
      await mutate();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Could not mark attendance';
      toast.error(message);
      await mutate();
    } finally {
      setSubmittingId(null);
    }
  };

  return (
    <Card className="bg-primary/5 border-primary/20">
      <CardHeader>
        <CardTitle className="text-primary flex items-center gap-2">
          <TicketIcon className="h-5 w-5" />
          {isSelfAttending ? 'You are going!' : 'Your registrations'}
        </CardTitle>
        <CardDescription>
          {isSelfAttending
            ? 'You have registered for this event.'
            : 'You have tickets registered for this event.'}
        </CardDescription>
      </CardHeader>
      <CardFooter className="pt-6">
        <div className="w-full flex flex-col gap-3">
          <Button variant="outline" onClick={onViewTicket} className="w-full">
            <Eye className="mr-2 h-4 w-4" />
            {registrationsCount > 1 ? 'View My Tickets' : 'View My Ticket'}
          </Button>

          {selfMarkActivities.length > 0 ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2 px-0.5">
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Attendance
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  aria-label="Refresh attendance"
                  disabled={isRefreshing || isValidating}
                  onClick={() => void refreshAttendance()}
                >
                  <RefreshCw
                    className={cn('h-3.5 w-3.5', (isRefreshing || isValidating) && 'animate-spin')}
                  />
                </Button>
              </div>
              {selfMarkActivities.map((activity) => {
                const label = activity.selfMarkButtonLabel || "I'm here";
                const showActivityName = selfMarkActivities.length > 1;

                if (activity.myScan?.status === 'CONFIRMED') {
                  return (
                    <div
                      key={activity.id}
                      className="flex items-center gap-3 rounded-xl border border-green-500/30 bg-green-500/10 px-4 py-3"
                    >
                      <CheckCircle2 className="h-5 w-5 shrink-0 text-green-600" />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm text-foreground">Checked in</p>
                        {showActivityName ? (
                          <p className="text-xs text-muted-foreground truncate">{activity.name}</p>
                        ) : null}
                      </div>
                    </div>
                  );
                }

                if (activity.myScan?.status === 'PENDING') {
                  return (
                    <div
                      key={activity.id}
                      className="flex items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3"
                    >
                      <Clock3 className="h-5 w-5 shrink-0 text-amber-600" />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm text-foreground">
                          Waiting for confirmation
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {showActivityName ? `${activity.name} · ` : ''}A scanner manager will
                          confirm you
                        </p>
                      </div>
                    </div>
                  );
                }

                return (
                  <Button
                    key={activity.id}
                    className="w-full"
                    disabled={submittingId === activity.id}
                    onClick={() => void handleSelfMark(activity)}
                  >
                    {submittingId === activity.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        {label}
                        {showActivityName ? ` · ${activity.name}` : ''}
                      </>
                    )}
                  </Button>
                );
              })}
            </div>
          ) : null}
        </div>
      </CardFooter>
    </Card>
  );
}
